import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getKcpCertResult, postKcpCertStart } from '../../services/Api';
import { detectDevice } from '../../utils/device';
import { BASE_API } from '../constants/app';

// NHN KCP 휴대폰 본인인증
// - PC: mở popup, backend callback postMessage { type: 'KCP_CERT_RESULT', ordrIdxx, status } về cửa sổ này
// - Mobile: chuyển cả trang sang KCP, quay về /kcp-cert/result (KcpCertRedirect) rồi trở lại trang gọi
export const KCP_CERT_STORAGE_KEY = 'kcpCertReturn';

const API_ORIGIN = new URL(BASE_API).origin;
const POPUP_NAME = 'kcpCertPopup';

const isMobileDevice = () => ['android', 'ios'].includes(detectDevice());

export const readKcpCertStorage = () => {
    try {
        return JSON.parse(sessionStorage.getItem(KCP_CERT_STORAGE_KEY)) || {};
    } catch (e) {
        return {};
    }
};

// Kết quả /result -> các field form đăng ký (PADAA010, PADAC010)
export const toKcpCertFormData = (result) => {
    const birth = moment(result.birthDay, 'YYYYMMDD', true);
    const digits = (result.phoneNo || '').replace(/\D/g, '');
    const middleLength = digits.length === 10 ? 3 : 4;
    const part1 = digits.slice(0, 3);
    const part2 = digits.slice(3, 3 + middleLength);
    const part3 = digits.slice(3 + middleLength);

    return {
        nameKor: result.userName || '',
        birthYear: birth.isValid() ? birth.format('YYYY') : '',
        birthMonth: birth.isValid() ? birth.format('MM') : '',
        birthDay: birth.isValid() ? birth.format('DD') : '',
        residentNo: birth.isValid() ? birth.format('YYMMDD') : '',
        mobileNo: `${part1}-${part2}-${part3}`,
        mobilePhonePart1: part1,
        mobilePhonePart2: part2,
        mobilePhonePart3: part3,
        kcpOrdrIdxx: result.ordrIdxx,
    };
};

const submitToKcp = ({ callUrl, regCertKey, kcpPageSubmitYn }, target) => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = callUrl;
    form.target = target;
    [
        ['reg_cert_key', regCertKey],
        ['kcp_page_submit_yn', kcpPageSubmitYn],
    ].forEach(([name, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
    });
    document.body.appendChild(form);
    form.submit();
    form.remove();
};

const alertStartError = (error) => {
    console.log(error);
    window.alert(error?.response?.data?.message || '본인인증을 시작할 수 없습니다.\n잠시 후 다시 시도해 주세요.');
};

// popupOnly: luôn dùng popup kể cả mobile - cho màn hình không được mất state khi chuyển trang
// start() ở chế độ popup trả về true nếu đã mở popup và gửi yêu cầu tới KCP, false nếu popup bị chặn / lỗi
export default function useKcpCert({ onComplete, popupOnly = false }) {
    const [loading, setLoading] = useState(false);
    const onCompleteRef = useRef(onComplete);
    const pendingRef = useRef(null);
    const popupTimerRef = useRef(null);

    useEffect(() => {
        onCompleteRef.current = onComplete;
    }, [onComplete]);

    // silentIfPending: gọi khi popup bị đóng - nếu người dùng đóng giữa chừng (PENDING) thì không báo lỗi
    const finish = useCallback((ordrIdxx, { silentIfPending = false } = {}) => {
        clearInterval(popupTimerRef.current);
        pendingRef.current = null;
        setLoading(true);
        getKcpCertResult(ordrIdxx)
            .then(({ data }) => {
                if (silentIfPending && data?.status === 'PENDING') return;
                onCompleteRef.current?.(data);
            })
            .catch((error) => {
                console.log(error);
                if (silentIfPending) return;
                window.alert(error?.response?.data?.message || '본인인증 결과를 확인할 수 없습니다.');
            })
            .finally(() => setLoading(false));
    }, []);

    // PC: nhận kết quả từ popup
    useEffect(() => {
        const handleMessage = (event) => {
            if (event.origin !== API_ORIGIN || event.data?.type !== 'KCP_CERT_RESULT') return;
            if (!pendingRef.current) return;
            // Callback bị từ chối (không xác định được giao dịch) sẽ không có ordrIdxx -> vẫn kết thúc giao dịch đang chờ
            if (event.data.ordrIdxx && event.data.ordrIdxx !== pendingRef.current) return;
            finish(pendingRef.current);
        };
        window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('message', handleMessage);
            clearInterval(popupTimerRef.current);
        };
    }, [finish]);

    // Mobile: KcpCertRedirect đã lưu ordrIdxx trước khi quay lại trang này
    useEffect(() => {
        const { ordrIdxx } = readKcpCertStorage();
        if (ordrIdxx) {
            sessionStorage.removeItem(KCP_CERT_STORAGE_KEY);
            finish(ordrIdxx);
        }
    }, [finish]);

    const start = useCallback(async () => {
        if (!popupOnly && isMobileDevice()) {
            setLoading(true);
            try {
                const { data } = await postKcpCertStart({ pageSubmit: true });
                sessionStorage.setItem(
                    KCP_CERT_STORAGE_KEY,
                    JSON.stringify({ returnPath: window.location.pathname + window.location.search }),
                );
                submitToKcp(data, '_self');
            } catch (error) {
                alertStartError(error);
                setLoading(false);
            }
            return;
        }

        // Mở popup ngay trong sự kiện click để không bị trình duyệt chặn
        // Kích thước theo sample KCP (410 x 500), căn giữa màn hình
        const width = 410;
        const height = 500;
        const left = Math.max(0, (window.screen.width - width) / 2);
        const top = Math.max(0, (window.screen.height - height) / 2);
        const popup = window.open(
            '',
            POPUP_NAME,
            `width=${width},height=${height},left=${left},top=${top},toolbar=no,status=no,menubar=no,scrollbars=yes`,
        );
        if (!popup) {
            window.alert('팝업이 차단되었습니다.\n팝업 차단을 해제한 후 다시 시도해 주세요.');
            return false;
        }
        setLoading(true);
        try {
            const { data } = await postKcpCertStart({ pageSubmit: false });
            pendingRef.current = data.ordrIdxx;
            submitToKcp(data, POPUP_NAME);

            // Popup đóng mà vẫn đang chờ giao dịch -> tự hỏi kết quả.
            // Dự phòng khi postMessage không tới được (front-origin không khớp, mất window.opener...).
            // Đợi một chút để message (nếu có) được xử lý trước.
            const { ordrIdxx } = data;
            clearInterval(popupTimerRef.current);
            popupTimerRef.current = setInterval(() => {
                if (!popup.closed) return;
                clearInterval(popupTimerRef.current);
                setTimeout(() => {
                    if (pendingRef.current === ordrIdxx) {
                        finish(ordrIdxx, { silentIfPending: true });
                    }
                }, 300);
            }, 500);
            return true;
        } catch (error) {
            popup.close();
            alertStartError(error);
            setLoading(false);
            return false;
        }
    }, [popupOnly, finish]);

    return { start, loading };
}
